import { onLCP } from "web-vitals";

// Metric interface for web-vitals (matching LCPMetric)
interface Metric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  entries: PerformanceEntry[];
  id: string;
  navigationType:
    | "navigate"
    | "reload"
    | "back-forward"
    | "prerender"
    | "back-forward-cache"
    | "restore";
}

// Performance metrics interface for Mixpanel integration
export interface PerformanceMetrics {
  lcp?: number;
}

// LCP measurement result with error handling
export interface LCPResult {
  value: number;
  errorMessage?: string;
  measurementType: "measured" | "timeout_fallback" | "measurement_failed";
}

// Callback function type for when LCP is measured
export type LCPCallback = (result: LCPResult) => void;

/**
 * Measures the Largest Contentful Paint (LCP) metric with percentile tracking
 * Uses web-vitals library with optimized configuration for minimal performance impact
 *
 * @param callback - Function to call when LCP result is available
 * @param options - Configuration options for LCP measurement
 */
export const measureLCP = (
  callback: LCPCallback,
  options?: {
    reportAllChanges?: boolean;
    timeout?: number;
    fallbackValue?: number;
  },
): void => {
  // Only run on client-side - non-blocking check
  if (typeof window === "undefined") {
    return;
  }

  // Check for basic browser support - non-blocking
  if (!window.PerformanceObserver || !window.performance) {
    const errorMsg =
      "Browser does not support Performance Observer API for LCP measurement";
    console.warn(errorMsg);

    // Provide fallback result if specified - non-blocking
    if (
      options?.fallbackValue !== undefined &&
      typeof callback === "function"
    ) {
      const fallbackValue = options.fallbackValue;
      setTimeout(() => {
        callback({
          value: fallbackValue,
          measurementType: "measurement_failed",
          errorMessage: errorMsg,
        });
      }, 0);
    }
    return;
  }

  let hasReported = false;
  const timeout = options?.timeout || 10000; // Default 10 second timeout

  // Set up timeout fallback - non-blocking
  const timeoutId = setTimeout(() => {
    if (!hasReported && options?.fallbackValue !== undefined) {
      hasReported = true;
      const timeoutMsg = "LCP measurement timed out, using fallback value";
      console.warn(timeoutMsg);

      if (typeof callback === "function") {
        const fallbackValue = options.fallbackValue;
        callback({
          value: fallbackValue,
          measurementType: "timeout_fallback",
          errorMessage: timeoutMsg,
        });
      }
    }
  }, timeout);

  try {
    onLCP(
      (metric: Metric) => {
        // Non-blocking duplicate check
        if (hasReported) return;

        try {
          // Validate metric value - non-blocking
          if (
            typeof metric?.value !== "number" ||
            isNaN(metric.value) ||
            metric.value < 0
          ) {
            const errorMsg = `Invalid LCP metric value: ${metric?.value}`;
            console.warn(errorMsg, "metric:", metric);
            return;
          }

          // Round to nearest millisecond for cleaner data - non-blocking
          const lcpValue = Math.round(metric.value);

          // Only report final value to avoid spam - non-blocking
          if (options?.reportAllChanges !== true && metric.delta > 0) {
            return; // Wait for final value
          }

          // Mark as reported and clear timeout - non-blocking
          hasReported = true;
          clearTimeout(timeoutId);

          // Safely execute callback - non-blocking
          if (typeof callback === "function") {
            callback({
              value: lcpValue,
              measurementType: "measured",
            });
          }
        } catch (callbackError) {
          // Prevent callback errors from breaking the page - non-blocking
          const errorMsg = `LCP callback error: ${
            callbackError instanceof Error
              ? callbackError.message
              : String(callbackError)
          }`;
          console.warn(errorMsg);

          // Still try to report with error - non-blocking
          if (!hasReported && typeof callback === "function") {
            hasReported = true;
            clearTimeout(timeoutId);
            callback({
              value: options?.fallbackValue ?? 3000,
              measurementType: "measurement_failed",
              errorMessage: errorMsg,
            });
          }
        }
      },
      {
        // Report all changes to capture more events, especially for dynamic content
        reportAllChanges: options?.reportAllChanges !== false, // Default to true
      },
    );
  } catch (error) {
    // Clear timeout on error - non-blocking
    clearTimeout(timeoutId);

    const errorMsg = `Failed to measure LCP: ${
      error instanceof Error ? error.message : String(error)
    }`;
    console.warn(errorMsg);

    // Provide fallback result if specified - non-blocking
    if (!hasReported && typeof callback === "function") {
      hasReported = true;
      setTimeout(() => {
        callback({
          value: options?.fallbackValue ?? 3000,
          measurementType: "measurement_failed",
          errorMessage: errorMsg,
        });
      }, 0);
    }
  }
};

/**
 * Creates a promise-based LCP measurement
 * Useful for async/await patterns
 *
 * @param timeout - Maximum time to wait for LCP measurement (default: 10 seconds)
 * @returns Promise that resolves with LCP result or null if timeout/error
 */
export const getLCPAsync = (timeout = 10000): Promise<LCPResult | null> => {
  return new Promise(resolve => {
    let isResolved = false;

    // Set timeout to avoid hanging - non-blocking
    const timeoutId = setTimeout(() => {
      if (!isResolved) {
        isResolved = true;
        resolve(null);
      }
    }, timeout);

    measureLCP(result => {
      if (!isResolved) {
        isResolved = true;
        clearTimeout(timeoutId);
        resolve(result);
      }
    });
  });
};
